import { useState, useEffect } from "react";
import {
  Box,
  Text,
  Flex,
  Button,
  Divider,
  Collapse,
  Grid,
  GridItem,
  Input,
  Select,
  Badge,
  useToast,
} from "@chakra-ui/react";
import printDetails from "../../utils/printDetails";
import { API_BASE_URL } from "../../config/api";
import axiosInstance from "../../utils/axiosInstance";

const todayISO = () => new Date().toISOString().split("T")[0];

// Fields that can be inline-edited (weight/quantity are auto, location has its own move flow)
const EDITABLE_FIELDS = [
  "lot",
  "vendor",
  "brand",
  "species",
  "description",
  "grade",
  "type",
  "packdate",
  "date_recvd",
  "est",
  "price",
];

const species = ["BEEF", "PORK", "CHICKEN", "LAMB", "OTHER"];

const EditableField = ({ label, value, fieldKey, onStage }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");


  const startEdit = () => {
    if (!EDITABLE_FIELDS.includes(fieldKey)) return;
    setDraft(value || "");
    setEditing(true);
  };

  const commit = () => {
    const processedDraft = fieldKey === "species" ? draft.toUpperCase() : draft;
    if (processedDraft !== (value || "") && (fieldKey !== "species" || species.includes(processedDraft))) onStage(fieldKey, label, value || "", processedDraft);
    setEditing(false);
  };

  const cancel = () => setEditing(false);

  if (editing) {
    return (
      <GridItem>
        <Text
          fontSize="xs"
          color="gray.400"
          fontWeight="medium"
          textTransform="uppercase"
          letterSpacing="wide"
          mb={1}
        >
          {label}
        </Text>
        {fieldKey === "type" ? (
          <Select size="xs" value={draft} autoFocus borderRadius="md" onChange={(e) => setDraft(e.target.value)} onBlur={commit}>
            <option value="">—</option>
            <option value="raw">Raw</option>
            <option value="prc">Processed</option>
          </Select>
        ) : (
          <Input
            size="xs"
            value={draft}
            autoFocus
            borderRadius="md"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") cancel();
            }}
            onBlur={commit}
          />
        )}
      </GridItem>
    );
  }

  const isEditable = EDITABLE_FIELDS.includes(fieldKey);
  return (
    <GridItem>
      <Text
        fontSize="xs"
        color="gray.400"
        fontWeight="medium"
        textTransform="uppercase"
        letterSpacing="wide"
      >
        {label}
      </Text>
      {fieldKey === "type" ? (
        <Badge
          colorScheme={value === "raw" ? "green" : value === "prc" ? "teal" : "gray"}
          fontSize="xs"
          cursor={isEditable ? "pointer" : "default"}
          onDoubleClick={isEditable ? startEdit : undefined}
          title={isEditable ? "Double-click to edit" : undefined}
        >
          {value === "raw" ? "Raw" : value === "prc" ? "Processed" : "—"}
        </Badge>
      ) : (
        <Text
          fontSize="sm"
          color={value ? "gray.700" : "gray.300"}
          fontWeight="medium"
          cursor={isEditable ? "text" : "default"}
          title={isEditable ? "Double-click to edit" : undefined}
          onDoubleClick={isEditable ? startEdit : undefined}
          _hover={isEditable ? { color: "blue.500" } : undefined}
          transition="color 0.1s"
        >
          {value || "—"}
        </Text>
      )}
    </GridItem>
  );
};

const DetailsPanel = ({
  item,
  showDetails,
  onClose,
  onSet,
  onLocate,
  onItemUpdate,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [addingBox, setAddingBox] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkCount, setBulkCount] = useState("");
  const [bulkWeight, setBulkWeight] = useState("");
  const [newBoxWeight, setNewBoxWeight] = useState("");
  const [boxLoading, setBoxLoading] = useState(null);
  const [boxesExpanded, setBoxesExpanded] = useState(false);
  const [selectedBoxes, setSelectedBoxes] = useState([]);
  const [lastRemovedBoxes, setLastRemovedBoxes] = useState(null);
  const [pendingEdit, setPendingEdit] = useState(null); // { field, label, oldValue, newValue }
  const [saving, setSaving] = useState(false);
  const [prodExpanded, setProdExpanded] = useState(false);
  const [prodHistory, setProdHistory] = useState(null);
  const [prodLoading, setProdLoading] = useState(false);
  const [noblesseOpen, setNoblesseOpen] = useState(false);
  const [noblesseIndices, setNoblesseIndices] = useState([]);
  const [noblesseSentDate, setNoblesseSentDate] = useState(todayISO);
  const [noblesseSending, setNoblesseSending] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnRows, setReturnRows] = useState([{ lot: "", location: "", weight: "", boxes: [], bCount: "", bWeight: "" }]);
  const [returnSending, setReturnSending] = useState(false);
  const toast = useToast();
  const token = localStorage.getItem("token");

  const authPatch = (url, body) =>
    fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: token || "",
      },
      body: JSON.stringify(body),
    });

  const toggleProd = async () => {
    if (prodExpanded) { setProdExpanded(false); return; }
    setProdExpanded(true);
    if (prodHistory) return;
    setProdLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/inventory/${item._id}/production`, {
        headers: { Authorization: token || "" },
      });
      const data = await res.json();
      setProdHistory(data);
    } catch {
      setProdHistory({ outgoing: [], incoming: [] });
    } finally {
      setProdLoading(false);
    }
  };

  const handleStage = (field, label, oldValue, newValue) => {
    if (newValue.trim() === oldValue.trim()) return;
    setPendingEdit({ field, label, oldValue, newValue });
  };

  const handleConfirmEdit = async () => {
    if (!pendingEdit) return;
    setSaving(true);
    try {
      const res = await authPatch(
        `${API_BASE_URL}/inventory/${item._id}/field`,
        { field: pendingEdit.field, value: pendingEdit.newValue },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      onItemUpdate(data);
      setPendingEdit(null);
    } catch (err) {
      toast({
        title: "Failed to update",
        description: err.message,
        status: "error",
        position: "top",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveBox = async (index) => {
    setBoxLoading(index);
    try {
      const removedWeight = item.boxes[index].weight;
      const res = await authPatch(
        `${API_BASE_URL}/inventory/${item._id}/box/remove`,
        { index },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      onItemUpdate(data);
      setLastRemovedBoxes({ boxes: [{ weight: removedWeight }] });
    } catch (err) {
      toast({
        title: "Failed to remove box",
        description: err.message,
        status: "error",
        position: "top",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setBoxLoading(null);
    }
  };

  const handleBatchRemoveBoxes = async () => {
    if (selectedBoxes.length === 0) return;
    setBoxLoading("batch");
    try {
      const removedBoxes = selectedBoxes.map(i => item.boxes[i]);
      const res = await authPatch(
        `${API_BASE_URL}/inventory/${item._id}/box/bulk-remove`,
        { indices: selectedBoxes },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      onItemUpdate(data);
      setLastRemovedBoxes({ boxes: removedBoxes });
      setSelectedBoxes([]);
      toast({
        title: "Boxes removed",
        description: `Successfully removed ${removedBoxes.length} box(es)`,
        status: "success",
        position: "top",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Failed to remove boxes",
        description: err.message,
        status: "error",
        position: "top",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setBoxLoading(null);
    }
  };

  const handleUndoRemove = async () => {
    if (!lastRemovedBoxes || !lastRemovedBoxes.boxes || lastRemovedBoxes.boxes.length === 0) return;
    setBoxLoading("undo");
    try {
      const boxes = lastRemovedBoxes.boxes;

      if (boxes.length === 1) {
        const res = await authPatch(
          `${API_BASE_URL}/inventory/${item._id}/box/add`,
          { weight: boxes[0].weight },
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed");
        onItemUpdate(data);
      } else {
        const res = await authPatch(
          `${API_BASE_URL}/inventory/${item._id}/box/bulk-add`,
          { count: boxes.length, weight: boxes[0].weight },
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed");
        onItemUpdate(data);
      }
      setLastRemovedBoxes(null);
    } catch (err) {
      toast({
        title: "Failed to undo",
        description: err.message,
        status: "error",
        position: "top",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setBoxLoading(null);
    }
  };

  const handleAddBox = async () => {
    const w = parseFloat(newBoxWeight);
    if (isNaN(w) || w <= 0) {
      toast({
        title: "Enter a valid weight",
        status: "warning",
        position: "top",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    setBoxLoading("add");
    try {
      const res = await authPatch(
        `${API_BASE_URL}/inventory/${item._id}/box/add`,
        { weight: newBoxWeight },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      onItemUpdate(data);
      setNewBoxWeight("");
      setAddingBox(false);
    } catch (err) {
      toast({
        title: "Failed to add box",
        description: err.message,
        status: "error",
        position: "top",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setBoxLoading(null);
    }
  };

  const handleBulkAdd = async () => {
    const n = parseInt(bulkCount);
    const w = parseFloat(bulkWeight);
    if (!n || n <= 0) { toast({ title: "Enter a valid count", status: "warning", position: "top", duration: 2000, isClosable: true }); return; }
    if (!w || w <= 0) { toast({ title: "Enter a valid weight", status: "warning", position: "top", duration: 2000, isClosable: true }); return; }
    setBoxLoading("bulk");
    try {
      const res = await authPatch(`${API_BASE_URL}/inventory/${item._id}/box/bulk-add`, { count: n, weight: w });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      onItemUpdate(data);
      setBulkCount("");
      setBulkWeight("");
      setBulkMode(false);
      setAddingBox(false);
    } catch (err) {
      toast({ title: "Failed to bulk add", description: err.message, status: "error", position: "top", duration: 3000, isClosable: true });
    } finally {
      setBoxLoading(null);
    }
  };

  const toggleNoblesseIndex = (i) => {
    setNoblesseIndices((prev) =>
      prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]
    );
  };

  const handleSendToNoblesse = async () => {
    const boxes = item?.boxes ?? [];
    let boxesSent, weightSent;
    if (boxes.length > 0) {
      if (noblesseIndices.length === 0) return;
      boxesSent = noblesseIndices.map((i) => boxes[i]);
      weightSent = boxesSent.reduce((s, b) => s + parseFloat(b.weight || 0), 0);
    } else {
      // No box records — send full pallet weight as one entry
      const w = parseFloat(item?.weight || 0);
      if (!w) return;
      boxesSent = [{ weight: String(w) }];
      weightSent = w;
    }
    setNoblesseSending(true);
    try {
      await axiosInstance.post("/production-orders", {
        sentDate: noblesseSentDate,
        processorName: "Noblesse Trading",
        items: [{ inventoryId: item._id, weightSent, boxesSent }],
      });
      toast({ title: "Sent to Noblesse Trading", status: "success", position: "top", duration: 3000, isClosable: true });
      setNoblesseOpen(false);
      setNoblesseIndices([]);
      setProdHistory(null); // stale, will reload on next expand
    } catch (err) {
      toast({ title: err.response?.data?.error || "Failed to create order", status: "error", position: "top", duration: 4000, isClosable: true });
    } finally {
      setNoblesseSending(false);
    }
  };

  const emptyReturnRow = (lot = "") => ({ lot, location: "", description: item?.description || "", weight: "", boxes: [], bCount: "", bWeight: "" });

  const updateReturnRow = (i, field, value) =>
    setReturnRows((prev) => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));

  const addReturnRow = () =>
    setReturnRows((prev) => [...prev, emptyReturnRow(item?.lot || "")]);

  const removeReturnRow = (i) =>
    setReturnRows((prev) => prev.filter((_, idx) => idx !== i));

  const addBoxesToRow = (i) => {
    setReturnRows((prev) => prev.map((r, idx) => {
      if (idx !== i) return r;
      const n = parseInt(r.bCount);
      const w = parseFloat(r.bWeight);
      if (!n || n <= 0 || !w || w <= 0) return r;
      const newBoxes = [...r.boxes, ...Array.from({ length: n }, () => ({ weight: String(w) }))];
      const totalWeight = newBoxes.reduce((s, b) => s + parseFloat(b.weight), 0).toFixed(2);
      return { ...r, boxes: newBoxes, weight: totalWeight, bCount: "", bWeight: "" };
    }));
  };

  const removeBoxFromRow = (rowIdx, boxIdx) => {
    setReturnRows((prev) => prev.map((r, idx) => {
      if (idx !== rowIdx) return r;
      const newBoxes = r.boxes.filter((_, bi) => bi !== boxIdx);
      const totalWeight = newBoxes.length > 0
        ? newBoxes.reduce((s, b) => s + parseFloat(b.weight), 0).toFixed(2) : "";
      return { ...r, boxes: newBoxes, weight: totalWeight };
    }));
  };

  const handleConfirmReturn = async () => {
    const validRows = returnRows.filter((r) => r.location && (r.weight || r.boxes.length > 0));
    if (!validRows.length) return;
    setReturnSending(true);
    try {
      for (const row of validRows) {
        const res = await fetch(`${API_BASE_URL}/inventoryAdd`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token || "" },
          body: JSON.stringify({
            inputs: {
              location: row.location,
              lot: row.lot || item.lot,
              vendor: item.vendor,
              brand: item.brand,
              species: item.species,
              description: row.description,
              boxes: row.boxes,
              weight: row.boxes.length ? undefined : row.weight,
              type: "prc",
              source_id: item._id,
            },
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed");
      }
      toast({ title: `${validRows.length} item${validRows.length > 1 ? "s" : ""} returned`, status: "success", position: "top", duration: 3000, isClosable: true });
      setReturnOpen(false);
      setReturnRows([emptyReturnRow()]);
    } catch (err) {
      toast({ title: "Return failed", description: err.message, status: "error", position: "top", duration: 4000, isClosable: true });
    } finally {
      setReturnSending(false);
    }
  };

  const itemBoxes = item?.boxes ?? [];

  useEffect(() => {
    setProdHistory(null);
    setProdExpanded(false);
    setNoblesseOpen(false);
    setNoblesseIndices([]);
    setNoblesseSentDate(todayISO());
    setReturnOpen(false);
    setReturnRows([{ lot: "", location: "", weight: "", boxes: [], bCount: "", bWeight: "" }]);
    setSelectedBoxes([]);
    setLastRemovedBoxes(null);
  }, [item?._id]);

  return (
    <Collapse
      in={showDetails && !!item}
      animateOpacity
      onAnimationComplete={() => {
        if (!showDetails) setExpanded(false);
      }}
    >
      <Box
        borderTop="2px"
        borderColor="blue.100"
        bg="white"
        px={4}
        pt={3}
        pb={4}
        maxH="55vh"
        overflowY="auto"
      >
        <Flex justify="space-between" align="flex-start" mb={2}>
          <Box>
            <Text fontWeight="bold" fontSize="sm" color="gray.800">
              {item?.location}
            </Text>
            {/* <Text fontSize="xs" color="gray.500" noOfLines={1}>
              {item?.description}
            </Text> */}
            <EditableField
            // label="Description"
            value={item?.description}
            fieldKey="description"
            onStage={handleStage}
          />
          </Box>
          <Button
            size="xs"
            variant="ghost"
            color="gray.400"
            _hover={{ color: "gray.700" }}
            onClick={onClose}
            minW="auto"
            p={1}
          >
            ✕
          </Button>
        </Flex>

        <Divider mb={3} />

        {/* Pending edit confirmation bar */}
        {pendingEdit && (
          <Flex
            align="center"
            gap={2}
            mb={3}
            px={3}
            py={2}
            bg="blue.50"
            borderRadius="md"
            border="1px"
            borderColor="blue.200"
          >
            <Text fontSize="xs" color="blue.700" flex={1} noOfLines={1}>
              Set <b>{pendingEdit.label}</b> to "{pendingEdit.newValue}"?
            </Text>
            <Button
              size="xs"
              colorScheme="blue"
              borderRadius="md"
              isLoading={saving}
              onClick={handleConfirmEdit}
            >
              Confirm
            </Button>
            <Button
              size="xs"
              variant="ghost"
              colorScheme="gray"
              onClick={() => setPendingEdit(null)}
            >
              ✕
            </Button>
          </Flex>
        )}

        {/* Summary row — always visible */}
        <Grid templateColumns="repeat(3, 1fr)" gap={3} mb={3}>
          <EditableField
            label="Lot"
            value={item?.lot}
            fieldKey="lot"
            onStage={handleStage}
          />
          <EditableField
            label="Brand"
            value={item?.brand}
            fieldKey="brand"
            onStage={handleStage}
          />
          <EditableField
            label="Grade"
            value={item?.grade}
            fieldKey="grade"
            onStage={handleStage}
          />
          <GridItem>
            <Text
              fontSize="xs"
              color="gray.400"
              fontWeight="medium"
              textTransform="uppercase"
              letterSpacing="wide"
            >
              Quantity
            </Text>
            <Text
              fontSize="sm"
              color={item?.quantity ? "gray.700" : "gray.300"}
              fontWeight="medium"
            >
              {item?.quantity ? `${item.quantity} bx` : "—"}
            </Text>
          </GridItem>
          <GridItem>
            <Text
              fontSize="xs"
              color="gray.400"
              fontWeight="medium"
              textTransform="uppercase"
              letterSpacing="wide"
            >
              Weight
            </Text>
            <Text
              fontSize="sm"
              color={item?.weight ? "gray.700" : "gray.300"}
              fontWeight="medium"
            >
              {item?.weight ? `${item.weight} lb` : "—"}
            </Text>
          </GridItem>
          <EditableField
            label="Species"
            value={item?.species}
            fieldKey="species"
            onStage={handleStage}
          />
          <EditableField
            label="Price / lb"
            value={item?.price ? `$${item.price}` : null}
            fieldKey="price"
            onStage={handleStage}
          />
          <EditableField
            label="Type"
            value={item?.type}
            fieldKey="type"
            onStage={handleStage}
          />
        </Grid>

        {/* Expanded details */}
        <Collapse in={expanded} animateOpacity>
          <Divider mb={3} />
          <Grid templateColumns="repeat(3, 1fr)" gap={3} mb={3}>
            <EditableField
              label="Vendor/Brand"
              value={item?.vendor}
              fieldKey="vendor"
              onStage={handleStage}
            />
            <EditableField
              label="EST #"
              value={item?.est}
              fieldKey="est"
              onStage={handleStage}
            />
            <EditableField
              label="Pack Date"
              value={item?.packdate}
              fieldKey="packdate"
              onStage={handleStage}
            />
            <EditableField
              label="Date Received"
              value={item?.date_recvd}
              fieldKey="date_recvd"
              onStage={handleStage}
            />
            <EditableField
              label="Description"
              value={item?.description}
              fieldKey="description"
              onStage={handleStage}
            />
          </Grid>
        </Collapse>

        {/* Boxes section — collapsible */}
        {item && (
          <>
            <Divider mb={3} />
            <Button
              size="xs"
              variant="ghost"
              color="gray.400"
              _hover={{ color: "gray.600" }}
              mb={boxesExpanded ? 2 : 0}
              onClick={() => {
                setBoxesExpanded((v) => !v);
                setAddingBox(false);
                setNewBoxWeight("");
                setLastRemovedBoxes(null);
                setSelectedBoxes([]);
              }}
            >
              {boxesExpanded
                ? "Hide Boxes"
                : `Boxes${itemBoxes.length > 0 ? ` (${itemBoxes.length})` : ""}`}
            </Button>
            <Collapse in={boxesExpanded} animateOpacity>
              <Box>
                {itemBoxes.length > 0 ? (
                  <>
                    <Flex gap={2} mb={2} align="center" flexWrap="wrap">
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="blue"
                        onClick={() => setSelectedBoxes(itemBoxes.map((_, i) => i))}
                      >
                        Select All
                      </Button>
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="gray"
                        onClick={() => setSelectedBoxes([])}
                      >
                        Clear
                      </Button>
                      {selectedBoxes.length > 0 && (
                        <>
                          <Text fontSize="xs" color="blue.600" fontWeight="medium">
                            {selectedBoxes.length} selected
                          </Text>
                          <Button
                            size="xs"
                            colorScheme="red"
                            variant="solid"
                            isLoading={boxLoading === "batch"}
                            onClick={handleBatchRemoveBoxes}
                          >
                            Delete Selected
                          </Button>
                        </>
                      )}
                    </Flex>
                    <Flex flexWrap="wrap" gap={1.5} mb={2}>
                      {itemBoxes.map((box, i) => {
                        const isSelected = selectedBoxes.includes(i);
                        return (
                          <Flex
                            key={i}
                            align="center"
                            gap={1}
                            px={2}
                            py={0.5}
                            bg={isSelected ? "blue.100" : "gray.100"}
                            border="1px"
                            borderColor={isSelected ? "blue.300" : "transparent"}
                            borderRadius="full"
                            fontSize="xs"
                            color={isSelected ? "blue.700" : "gray.600"}
                            cursor="pointer"
                            fontWeight={isSelected ? "semibold" : "normal"}
                            onClick={() => {
                              setSelectedBoxes((prev) =>
                                prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]
                              );
                            }}
                            _hover={{ borderColor: isSelected ? "blue.400" : "gray.300", bg: isSelected ? "blue.100" : "gray.50" }}
                            transition="all 0.1s"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}}
                              style={{ cursor: "pointer" }}
                            />
                            <Text>{parseFloat(box.weight).toFixed(2)} lb</Text>
                            {selectedBoxes.length === 0 && (
                              <Button
                                size="xs"
                                variant="ghost"
                                colorScheme="red"
                                minW="auto"
                                px={0.5}
                                h="auto"
                                lineHeight="1"
                                isLoading={boxLoading === i}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveBox(i);
                                }}
                                _hover={{ color: "red.500" }}
                              >
                                ×
                              </Button>
                            )}
                          </Flex>
                        );
                      })}
                    </Flex>
                  </>
                ) : (
                  <Text fontSize="xs" color="gray.400" mb={2}>
                    No boxes recorded
                  </Text>
                )}
                {lastRemovedBoxes && lastRemovedBoxes.boxes && lastRemovedBoxes.boxes.length > 0 && (
                  <Flex
                    align="center"
                    gap={2}
                    mb={2}
                    px={2}
                    py={1}
                    bg="orange.50"
                    borderRadius="md"
                    border="1px"
                    borderColor="orange.200"
                  >
                    <Text fontSize="xs" color="orange.700">
                      Removed {lastRemovedBoxes.boxes.length} box(es) ({lastRemovedBoxes.boxes.reduce((s, b) => s + parseFloat(b.weight || 0), 0).toFixed(2)} lb)
                    </Text>
                    <Button
                      size="xs"
                      colorScheme="orange"
                      variant="solid"
                      borderRadius="md"
                      isLoading={boxLoading === "undo"}
                      onClick={handleUndoRemove}
                    >
                      Undo
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="gray"
                      minW="auto"
                      px={1}
                      onClick={() => setLastRemovedBoxes(null)}
                    >
                      ✕
                    </Button>
                  </Flex>
                )}
                {addingBox ? (
                  <Flex direction="column" gap={2}>
                    <Flex gap={1} bg="gray.100" borderRadius="md" p={0.5} w="fit-content">
                      <Button size="xs" borderRadius="sm" bg={!bulkMode ? "white" : "transparent"} color={!bulkMode ? "gray.700" : "gray.400"} boxShadow={!bulkMode ? "sm" : "none"} onClick={() => setBulkMode(false)} _hover={{}}>Single</Button>
                      <Button size="xs" borderRadius="sm" bg={bulkMode ? "white" : "transparent"} color={bulkMode ? "blue.600" : "gray.400"} boxShadow={bulkMode ? "sm" : "none"} onClick={() => setBulkMode(true)} _hover={{}}>Bulk</Button>
                    </Flex>
                    {bulkMode ? (
                      <Flex gap={2} align="center" flexWrap="wrap">
                        <Input size="xs" placeholder="Count" value={bulkCount} onChange={(e) => setBulkCount(e.target.value)} borderRadius="md" w="70px" type="number" min={1} />
                        <Text fontSize="xs" color="gray.400">×</Text>
                        <Input size="xs" placeholder="lb each" value={bulkWeight} onChange={(e) => setBulkWeight(e.target.value)} borderRadius="md" w="80px" type="number" min={0} onKeyDown={(e) => { if (e.key === "Enter") handleBulkAdd(); }} />
                        <Button size="xs" colorScheme="blue" borderRadius="md" isLoading={boxLoading === "bulk"} onClick={handleBulkAdd}>Add</Button>
                        <Button size="xs" variant="ghost" colorScheme="gray" onClick={() => { setAddingBox(false); setBulkMode(false); setBulkCount(""); setBulkWeight(""); }}>Cancel</Button>
                      </Flex>
                    ) : (
                      <Flex gap={2}>
                        <Input size="xs" placeholder="Weight (lb)" value={newBoxWeight} onChange={(e) => setNewBoxWeight(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") handleAddBox(); }} borderRadius="md" w="120px" autoFocus />
                        <Button size="xs" colorScheme="blue" borderRadius="md" isLoading={boxLoading === "add"} onClick={handleAddBox}>Add</Button>
                        <Button size="xs" variant="ghost" colorScheme="gray" onClick={() => { setAddingBox(false); setNewBoxWeight(""); }}>Cancel</Button>
                      </Flex>
                    )}
                  </Flex>
                ) : (
                  <Button size="xs" variant="ghost" colorScheme="blue" onClick={() => setAddingBox(true)}>
                    + Add Box
                  </Button>
                )}
              </Box>
            </Collapse>
          </>
        )}

        {/* Production History */}
        <Divider mb={3} />
        <Button
          size="xs"
          variant="ghost"
          color="gray.400"
          _hover={{ color: "gray.600" }}
          mb={prodExpanded ? 2 : 0}
          onClick={toggleProd}
          isLoading={prodLoading}
        >
          {prodExpanded ? "Hide Production" : "Production"}
        </Button>
        <Collapse in={prodExpanded} animateOpacity>
          <Box fontSize="xs" color="gray.600">
            {(() => {
              const out = prodHistory?.outgoing ?? [];
              const inc = prodHistory?.incoming ?? [];
              if (!prodHistory) return null;
              if (out.length === 0 && inc.length === 0) {
                return <Text color="gray.400">No production history</Text>;
              }
              return (
                <>
                  {inc.length > 0 && (
                    <Box mb={3}>
                      <Text fontWeight="semibold" color="gray.500" mb={1} textTransform="uppercase" letterSpacing="wide" fontSize="10px">
                        Returned From
                      </Text>
                      {inc.map((r) => (
                        <Box key={r.orderId} px={2} py={1.5} bg="green.50" borderRadius="md" border="1px" borderColor="green.200" mb={1}>
                          <Text fontWeight="medium" color="gray.700">{r.processorName}</Text>
                          <Text color="gray.500">Sent {r.sentDate}{r.returnDate ? ` · Returned ${r.returnDate}` : ""}</Text>
                          {r.yieldPct && <Text color="gray.500">Yield: {r.yieldPct}%</Text>}
                        </Box>
                      ))}
                    </Box>
                  )}
                  {out.length > 0 && (
                    <Box>
                      <Text fontWeight="semibold" color="gray.500" mb={1} textTransform="uppercase" letterSpacing="wide" fontSize="10px">
                        Sent To
                      </Text>
                      {out.map((r) => (
                        <Box key={r.orderId} px={2} py={1.5} bg="blue.50" borderRadius="md" border="1px" borderColor="blue.200" mb={1}>
                          <Text fontWeight="medium" color="gray.700">{r.processorName}</Text>
                          <Text color="gray.500">
                            {r.sentDate} · {r.weightSent} lb · {r.boxesSent.length} box(es)
                          </Text>
                          <Badge size="xs" colorScheme={r.status === "returned" ? "green" : "orange"}>
                            {r.status}
                          </Badge>
                          {r.yieldPct && <Text color="gray.500" ml={1} display="inline">· Yield: {r.yieldPct}%</Text>}
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              );
            })()}
          </Box>
        </Collapse>

        {/* Send to Noblesse — only for CHILLING location */}
        {false && (item?.location?.toUpperCase() === "CHILLING" || item?.location?.toUpperCase() === "FLOOR") && (
          <>
            <Divider mb={3} />
            <Collapse in={noblesseOpen} animateOpacity>
              <Box bg="orange.50" border="1px" borderColor="orange.200" borderRadius="md" p={3} mb={2}>
                <Text fontSize="xs" fontWeight="semibold" color="orange.700" mb={2}>
                  Send to Noblesse Trading
                </Text>

                {itemBoxes.length > 0 ? (
                  <>
                    <Text fontSize="10px" color="gray.500" mb={1.5}>Select boxes to send:</Text>
                    <Flex flexWrap="wrap" gap={1.5} mb={2}>
                      {itemBoxes.map((box, i) => {
                        const sel = noblesseIndices.includes(i);
                        return (
                          <Flex
                            key={i}
                            align="center"
                            px={2}
                            py={0.5}
                            bg={sel ? "orange.100" : "gray.100"}
                            border="1px"
                            borderColor={sel ? "orange.400" : "gray.200"}
                            borderRadius="full"
                            fontSize="xs"
                            cursor="pointer"
                            onClick={() => toggleNoblesseIndex(i)}
                            color={sel ? "orange.700" : "gray.600"}
                            fontWeight={sel ? "semibold" : "normal"}
                          >
                            {parseFloat(box.weight).toFixed(2)} lb
                          </Flex>
                        );
                      })}
                    </Flex>
                    <Flex gap={2} mb={2} align="center">
                      <Button size="xs" variant="ghost" colorScheme="orange" onClick={() => setNoblesseIndices(itemBoxes.map((_, i) => i))}>All</Button>
                      <Button size="xs" variant="ghost" colorScheme="gray" onClick={() => setNoblesseIndices([])}>None</Button>
                      {noblesseIndices.length > 0 && (
                        <Text fontSize="xs" color="orange.600">
                          {noblesseIndices.length} boxes · {noblesseIndices.reduce((s, i) => s + parseFloat(itemBoxes[i]?.weight || 0), 0).toFixed(1)} lb
                        </Text>
                      )}
                    </Flex>
                  </>
                ) : (
                  <Text fontSize="xs" color="gray.500" mb={2}>
                    No individual boxes recorded — will send as full pallet ({item?.weight} lb)
                  </Text>
                )}

                <Flex align="center" gap={2} mb={3}>
                  <Text fontSize="xs" color="gray.500" flexShrink={0}>Send date:</Text>
                  <Input
                    size="xs"
                    type="date"
                    value={noblesseSentDate}
                    onChange={(e) => setNoblesseSentDate(e.target.value)}
                    w="130px"
                  />
                </Flex>

                <Flex gap={2}>
                  <Button
                    size="xs"
                    colorScheme="orange"
                    isLoading={noblesseSending}
                    isDisabled={itemBoxes.length > 0 && noblesseIndices.length === 0}
                    onClick={handleSendToNoblesse}
                  >
                    Confirm Send
                  </Button>
                </Flex>
              </Box>
            </Collapse>
            <Button
              size="xs"
              colorScheme={noblesseOpen ? "gray" : "orange"}
              variant="outline"
              mb={3}
              onClick={() => { setNoblesseOpen((v) => !v); setNoblesseIndices([]); }}
            >
              {noblesseOpen ? "Cancel" : "Send to Noblesse"}
            </Button>
          </>
        )}

        {/* Return from Noblesse — only when item is at Noblesse Trading */}
        {false && item?.location?.toUpperCase() === "NOBLESSE TRADING" && (
          <>
            <Divider mb={3} />
            <Collapse in={returnOpen} animateOpacity>
              <Box bg="green.50" border="1px" borderColor="green.200" borderRadius="md" p={3} mb={2}>
                <Text fontSize="xs" fontWeight="semibold" color="green.700" mb={2}>
                  Return from Noblesse Trading
                </Text>
                <Flex direction="column" gap={2}>
                  {returnRows.map((row, i) => (
                    <Box key={i} bg="white" border="1px" borderColor="green.100" borderRadius="md" p={2}>
                      {/* Lot + Location + remove */}
                      <Flex gap={1.5} align="center" mb={1.5} flexWrap="wrap">
                        <Input size="xs" placeholder="New Lot" value={row.lot} w="110px" borderRadius="md"
                          onChange={(e) => updateReturnRow(i, "lot", e.target.value)} />
                        <Input size="xs" placeholder="Location" value={row.location} w="80px" borderRadius="md"
                          onChange={(e) => updateReturnRow(i, "location", e.target.value)} />
                        {returnRows.length > 1 && (
                          <Button size="xs" variant="ghost" colorScheme="red" minW="auto" px={1} ml="auto"
                            onClick={() => removeReturnRow(i)}>×</Button>
                        )}
                      </Flex>
                      {/* Description */}
                      <Input size="xs" placeholder="Description" value={row.description} borderRadius="md" mb={1.5}
                        onChange={(e) => updateReturnRow(i, "description", e.target.value)} />
                      {/* Boxes chips */}
                      {row.boxes.length > 0 && (
                        <Flex flexWrap="wrap" gap={1} mb={1.5} align="center">
                          {row.boxes.map((box, bi) => (
                            <Flex key={bi} align="center" gap={0.5} px={1.5} py={0.5} bg="green.100" borderRadius="full" fontSize="xs" color="green.700">
                              <Text>{parseFloat(box.weight).toFixed(2)} lb</Text>
                              <Button size="xs" variant="ghost" colorScheme="red" minW="auto" px={0.5} h="auto" lineHeight="1"
                                onClick={() => removeBoxFromRow(i, bi)}>×</Button>
                            </Flex>
                          ))}
                          <Text fontSize="xs" color="green.600" ml={1}>= {row.weight} lb</Text>
                        </Flex>
                      )}
                      {/* Bulk add boxes */}
                      <Flex gap={1.5} align="center" flexWrap="wrap">
                        <Input size="xs" placeholder="Count" value={row.bCount} w="58px" borderRadius="md" type="number"
                          onChange={(e) => updateReturnRow(i, "bCount", e.target.value)} />
                        <Text fontSize="xs" color="gray.400">×</Text>
                        <Input size="xs" placeholder="lb each" value={row.bWeight} w="68px" borderRadius="md" type="number"
                          onChange={(e) => updateReturnRow(i, "bWeight", e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") addBoxesToRow(i); }} />
                        <Button size="xs" colorScheme="green" variant="outline" borderRadius="md"
                          onClick={() => addBoxesToRow(i)}>Add boxes</Button>
                        {!row.boxes.length && (
                          <Input size="xs" placeholder="or total lb" value={row.weight} w="80px" borderRadius="md" type="number"
                            onChange={(e) => updateReturnRow(i, "weight", e.target.value)} />
                        )}
                      </Flex>
                    </Box>
                  ))}
                </Flex>
                <Flex gap={2} mt={2} align="center">
                  <Button size="xs" variant="ghost" colorScheme="green" onClick={addReturnRow}>+ Add row</Button>
                  {returnRows.some((r) => r.location && (r.weight || r.boxes.length > 0)) && (
                    <Text fontSize="xs" color="green.600">
                      {returnRows.filter((r) => r.location && (r.weight || r.boxes.length > 0)).length} item(s) ·{" "}
                      {returnRows.reduce((s, r) => s + parseFloat(r.weight || 0), 0).toFixed(1)} lb
                    </Text>
                  )}
                </Flex>
                <Flex gap={2} mt={3}>
                  <Button
                    size="xs"
                    colorScheme="green"
                    isLoading={returnSending}
                    isDisabled={!returnRows.some((r) => r.location && (r.weight || r.boxes.length > 0))}
                    onClick={handleConfirmReturn}
                  >
                    Confirm Return
                  </Button>
                </Flex>
              </Box>
            </Collapse>
            <Button
              size="xs"
              colorScheme={returnOpen ? "gray" : "green"}
              variant="outline"
              mb={3}
              onClick={() => {
                if (!returnOpen) setReturnRows([emptyReturnRow(item?.lot || "")]);
                setReturnOpen((v) => !v);
              }}
            >
              {returnOpen ? "Cancel" : "Return from Noblesse"}
            </Button>
          </>
        )}

        <Flex gap={2} justify="space-between" align="center">
          <Button
            size="xs"
            variant="ghost"
            color="gray.400"
            _hover={{ color: "gray.600" }}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "Less" : "Full Details"}
          </Button>
          <Flex gap={2}>
            <Button
              size="xs"
              variant="outline"
              onClick={() => printDetails(item)}
            >
              Print
            </Button>
            <Button
              size="xs"
              colorScheme="orange"
              onClick={() => onLocate(item)}
            >
              Locate
            </Button>
            <Button size="xs" colorScheme="blue" onClick={() => onSet(item)}>
              Set
            </Button>
          </Flex>
        </Flex>
      </Box>
    </Collapse>
  );
};

export default DetailsPanel;
